import { useId } from 'react';
import { cn } from '@/lib/utils';

/** The product mark; `public/favicon.svg` draws the same path. */
const MARK =
  'M15.4574 12.1157H36.4501L22.3498 32.4757C20.6342 34.9536 16.9721 34.9536 15.2548 32.4757L9.54491 24.2297H15.8128V24.233L15.3146 25.9719L16.7479 25.0036C17.3723 24.5801 18.1911 24.5801 18.8172 25.0036L20.2505 25.9719L19.7722 24.3094C19.563 23.5836 19.8154 22.8047 20.4116 22.3414L21.7752 21.2768L20.0463 21.2186C19.2922 21.1921 18.6296 20.7104 18.3722 20.0013L17.7826 18.3753L17.1913 20.0013C17.1913 20.0013 17.1896 20.0079 17.188 20.0112C17.0086 20.4945 16.5286 20.8001 16.0121 20.8001H7.20648L0.996686 13.3231C-0.403383 11.639 -0.318681 9.1727 1.18934 7.58662L6.72816 1.76877C7.80271 0.639416 9.29246 0 10.852 0H24.0555C25.801 0 27.4884 0.616166 28.8237 1.73888L37.0829 8.68442H15.2066C14.6818 8.68442 14.1952 8.37218 14.0125 7.88058C14.0125 7.87726 14.0108 7.87394 14.0092 7.87062L13.4196 6.24468L12.83 7.87062C12.5709 8.57978 11.9083 9.06142 11.1542 9.08799L9.42699 9.14612L10.7905 10.2107C11.3851 10.6741 11.6375 11.453 11.4299 12.1788L10.9516 13.8413L12.3849 12.873C13.0094 12.4495 13.8282 12.4495 14.4543 12.873L15.8876 13.8413L15.4093 12.1788L15.4574 12.1157Z';

/**
 * The product logo. Its gradient runs dark → grey on light surfaces and light → grey on dark ones
 * (the original dark gradient would vanish there). `tone` pins one for surfaces that ignore the
 * theme, such as the always-dark sign-in screen.
 */
export function ProductLogo({
  className,
  tone = 'auto',
}: {
  className?: string;
  tone?: 'auto' | 'onLight' | 'onDark';
}) {
  // One gradient per instance: a shared id breaks when the first copy is hidden.
  const id = `logo-${useId().replace(/:/g, '')}`;
  const stops =
    tone === 'onDark'
      ? ['#ffffff', '#a3a3a3']
      : tone === 'onLight'
        ? ['#070014', '#4c4c4c']
        : ['var(--logo-from)', 'var(--logo-to)'];
  return (
    <svg
      viewBox="0 0 38 35"
      className={cn('h-6 w-auto shrink-0', className)}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient
          id={id}
          x1="18.54"
          y1="0"
          x2="18.54"
          y2="34.33"
          gradientUnits="userSpaceOnUse"
        >
          <stop style={{ stopColor: stops[0] }} />
          <stop offset="1" style={{ stopColor: stops[1] }} />
        </linearGradient>
      </defs>
      <path d={MARK} fill={`url(#${id})`} />
    </svg>
  );
}
